// @flow
import type { Saga } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { LOGOUT } from "redux/modules/auth";
import {
  GET_ME_REQUEST,
  GET_ME_REQUEST_SUCCESS,
} from "redux/modules/me/meActions";
import { LOADED } from "shared/constants/status";
import { loadInstructorLocations } from "redux/modules/businessEventScheduling/loadInstructorLocations";
import {
  REQUEST,
  getChapterLocations,
  receiveChapterLocations,
  chapterLocationsError,
} from "./instructorChapters";
import type { ChapterLocationsAction } from "./instructorChapters";

type IdentityState = {
  auth: { userId: ?string },
  me: {
    status: string,
    user?: ?{ id: string, instructor?: ?{ number?: ?string } },
  },
};
const getInstructorUserId = ({ auth, me }: IdentityState): ?string =>
  me.status === LOADED &&
  me.user?.id === auth.userId &&
  me.user?.instructor?.number
    ? auth.userId
    : null;

export function* performChapterLocations(
  action: ChapterLocationsAction
): Saga<void> {
  if (action.type !== REQUEST) {
    return;
  }
  const identity = action.payload;
  const userId = yield select(getInstructorUserId);
  if (!userId || userId !== identity.userId) {
    yield put(
      chapterLocationsError(
        "Sign in as an instructor to load your chapters.",
        identity
      )
    );
    return;
  }
  try {
    // Identity comes from the authenticated cookie. No user or business filter.
    const locations = yield call(loadInstructorLocations);
    yield put(receiveChapterLocations(locations, identity));
  } catch (error) {
    yield put(
      chapterLocationsError(
        error?.message || "Unable to load your chapters. Try again.",
        identity
      )
    );
  }
}

export function* discoverChaptersForInstructor(): Saga<void> {
  const userId = yield select(getInstructorUserId);
  if (userId) {
    yield put(getChapterLocations(userId));
  }
}

export function* watchChapterLocations(): Saga<void> {
  // Profile changes/logout also cancel requests issued under the old identity.
  yield takeLatest([REQUEST, GET_ME_REQUEST, LOGOUT], performChapterLocations);
  yield takeLatest(GET_ME_REQUEST_SUCCESS, discoverChaptersForInstructor);
}
